import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Subject } from 'rxjs';
import { Store } from '@ngrx/store';
import * as fromRoot from '../../../../state/app.state';
import { takeUntil } from 'rxjs/operators';
import { issueTypesLabelMap } from '../../../projects/core/projects.selectors';
import { ActivatedRoute } from '@angular/router';
import {
  highlightQueryTokens,
  matchedHintHtmlOf,
  searchEntityBySearchType,
} from '../../../../shared/constants/search-entities';
import { humanizeIssueType, IssueTypeLabelService } from '../../core/issue-type-label.service';
import { isIssueStageStatus } from '../../../../shared/helpers/issue-workflow';

@Component({
    selector: 'app-issue-card',
    templateUrl: './issue-card.component.html',
    styleUrls: ['./issue-card.component.scss'],
    standalone: false
})
export class IssueCardComponent implements OnInit, OnDestroy {
  @Input() issue: any;
  // Category icon from the registry (same one the header preview shows).
  readonly icon = searchEntityBySearchType['issues']?.icon;
  // Store map (project last opened) → per-project fetch → humanized type.
  issueTypesLabelMap: Record<string, string> = {};
  projectIssueTypeLabels: Record<string, string> = {};
  selectedId: any;
  matchedHintHtml: string;
  titleHtml: string;
  descriptionHtml: string;
  private readonly onDestroy: Subject<any> = new Subject<any>();

  constructor(
    private store: Store<fromRoot.State>,
    private route: ActivatedRoute,
    private issueTypeLabels: IssueTypeLabelService,
  ) {
  }

  ngOnInit() {
    this.matchedHintHtml = matchedHintHtmlOf(this.issue);
    this.titleHtml = highlightQueryTokens(this.issue?.title, this.issue?.searchQuery);
    this.descriptionHtml = highlightQueryTokens(this.issue?.description, this.issue?.searchQuery);

    this.store.select(issueTypesLabelMap)
      .pipe(takeUntil(this.onDestroy))
      .subscribe((d) => {
        if (Object.keys(d).length > 0) {
          this.issueTypesLabelMap = d;
        }
      });

    this.issueTypeLabels.labelsFor(this.issue?.project?.id)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(labels => {
        this.projectIssueTypeLabels = labels;
      });

    this.route.queryParams.pipe(takeUntil(this.onDestroy))
      .subscribe((qp) => {
        if (qp && qp['selectedId']) {
          this.selectedId = qp['selectedId'];
        }
      });
  }

  get issueTypeLabel(): string {
    const type = this.issue?.type;
    return this.issueTypesLabelMap?.[type]
      || this.projectIssueTypeLabels?.[type]
      || humanizeIssueType(type);
  }

  ngOnDestroy() {
    this.onDestroy.next(null);
    this.onDestroy.complete();
  }

  /**
   * The plain open / closed pill stands unless the status is a
   * contractor-workflow stage, which app-issue-stage-chip shows instead: one
   * status pill either way.
   */
  get isStageStatus(): boolean {
    return isIssueStageStatus(this.issue?.status);
  }
}
